import React, { useCallback, useEffect, useState } from "react";
import api, { downloadBalancesCsv } from "../../api";
import { toast } from "sonner";
import { Search, FileSpreadsheet, Info, FileUp, GraduationCap } from "lucide-react";
import { BalancesTable } from "./BalancesTable";
import { BalanceEditDialog } from "./BalanceEditDialog";
import { BalanceAuditLog } from "./BalanceAuditLog";
import { BalanceImportDialog } from "./BalanceImportDialog";
import { ConfirmDialog } from "./ConfirmDialog";
import { apiError } from "./balanceFields";

const THIS_YEAR = new Date().getFullYear();
const YEARS = [THIS_YEAR - 2, THIS_YEAR - 1, THIS_YEAR, THIS_YEAR + 1];

export const BalancesTab = ({ companyFilter = "all" }) => {
  const [year, setYear] = useState(THIS_YEAR);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [target, setTarget] = useState(null);
  const [auditEmp, setAuditEmp] = useState(null);
  const [auditKey, setAuditKey] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [confirmStudio, setConfirmStudio] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/balances?year=${year}&company=${companyFilter}`)
      .then((r) => setRows(r.data.rows))
      .catch(() => toast.error("Errore nel caricamento dei saldi"))
      .finally(() => setLoading(false));
  }, [year, companyFilter]);
  useEffect(() => { load(); }, [load]);

  const onSaved = (row) => {
    setRows((rs) => rs.map((r) => (r.id === row.id ? row : r)));
    setTarget(null);
    setAuditKey((k) => k + 1);
  };

  const showHistory = (r) => {
    setAuditEmp(r);
    setTimeout(() => document.getElementById("balance-audit")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  const exportCsv = async () => {
    setExporting(true);
    try { await downloadBalancesCsv(year, companyFilter); toast.success("Export saldi scaricato"); }
    catch { toast.error("Errore durante l'export"); }
    finally { setExporting(false); }
  };

  const onImported = () => { setShowImport(false); load(); setAuditKey((k) => k + 1); };

  const setStudio150 = async () => {
    try {
      const res = await api.post(`/balances/studio-150?company=${companyFilter}`);
      toast.success(`Permessi studio a 150 h per ${res.data.employees} dipendenti`);
      load(); setAuditKey((k) => k + 1);
    } catch (e) { toast.error(apiError(e, "Errore nell'aggiornamento")); }
    finally { setConfirmStudio(false); }
  };

  const q = search.toLowerCase();
  const filtered = rows.filter((r) => `${r.name} ${r.matricola} ${r.dept}`.toLowerCase().includes(q));

  return (
    <div data-testid="balances-tab">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="flex items-center border border-gray-300 rounded-sm px-3 bg-white flex-1 min-w-[220px]">
          <Search size={16} className="text-gray-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca dipendente..." data-testid="balances-search" className="flex-1 py-2 px-2 text-[14px] outline-none" />
        </div>
        <label className="flex items-center gap-2 border border-gray-300 rounded-sm px-3 bg-white text-[12px] font-bold text-gray-500">
          Anno straordinari
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="balances-year" className="py-2 text-[14px] font-bold text-[#16345c] outline-none bg-white cursor-pointer">
            {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </label>
        <button onClick={() => setShowImport(true)} data-testid="balances-import-open" className="flex items-center gap-2 bg-[#16345c] hover:bg-[#0d1b2f] text-white text-[13px] font-bold px-4 py-2.5 rounded-sm transition-colors">
          <FileUp size={15} /> Importa prospetto (PDF)
        </button>
        <button onClick={() => setConfirmStudio(true)} data-testid="balances-studio-150" className="flex items-center gap-2 border border-[#16345c] text-[#16345c] hover:bg-[#16345c]/5 text-[13px] font-bold px-4 py-2.5 rounded-sm transition-colors">
          <GraduationCap size={15} /> Permessi studio a 150
        </button>
        <button onClick={exportCsv} disabled={exporting} data-testid="balances-export-csv" className="flex items-center gap-2 bg-[#009640] hover:bg-[#007a34] text-white text-[13px] font-bold px-4 py-2.5 rounded-sm transition-colors disabled:opacity-60">
          <FileSpreadsheet size={15} /> {exporting ? "Export..." : "Esporta CSV/Excel"}
        </button>
      </div>
      <p className="flex items-center gap-1.5 text-[12.5px] text-gray-500 mb-4"><Info size={14} /> Clicca su un saldo per modificarlo: serve una motivazione e ogni modifica viene registrata nello storico.</p>
      <BalancesTable rows={filtered} loading={loading} year={year} onEdit={(row, col) => setTarget({ row, col })} onHistory={showHistory} />
      <BalanceAuditLog refreshKey={auditKey} employee={auditEmp} onClearEmployee={() => setAuditEmp(null)} />
      {showImport && <BalanceImportDialog onClose={() => setShowImport(false)} onApplied={onImported} />}
      <ConfirmDialog open={confirmStudio} onCancel={() => setConfirmStudio(false)} onConfirm={setStudio150} title="Permessi studio a 150 h"
        confirmLabel="Imposta a 150" confirmClass="bg-[#16345c] hover:bg-[#0d1b2f]">
        Imposti il saldo permessi studio a <b>150 ore</b> per tutti i dipendenti interni{companyFilter !== "all" ? " della società selezionata" : ""}? Le variazioni vengono registrate nello storico.
      </ConfirmDialog>
      {target && <BalanceEditDialog target={target} year={year} onClose={() => setTarget(null)} onSaved={onSaved} />}
    </div>
  );
};
